import solanaApi from '@exodus/solana-api';
import { PublicKey } from '@exodus/solana-web3.js';
import { deserializeTransaction } from '@exodus/web3-solana-utils';
import { SwapMode } from '../../common/types';
import { deconstructPairId, memoizeWithTimeout } from '../../utils';
import { DEFAULT_FEE_PERCENTAGE, DEFAULT_REFERRAL_PROGRAM_ADDRESS, DEFAULT_SLIPPAGE, NEW_TOKEN_ACCOUNT_FEE, ORIGIN, REFERRAL_PROGRAM_ID, SOL_ADDRESS, SUPPORTED_NETWORKS, SVC, Urls, WRAPPED_SOL_ADDRESS, } from './constants';
import { calculateTokenAccountsFee, promiseWithTimeout, updateBlockhash } from './utils';
import { parseFetchivalError, serializeQuoteKey } from '../utils';
import { QUOTE_CACHE_TTL } from '../constants';
import { CreateDexTxError, CreateOrderError, ExchangeError, OrderValidationError, SendTxError, SignTxError, } from '../../errors';
class Jupiter {
    name = 'jupiter';
    _assetsService;
    _ordersService;
    _pricingService;
    _fetchival;
    _feeBps;
    _referralProgramAddress;
    _tracer;
    _cachedQuotes = new Map();
    constructor(params) {
        this._assetsService = params.assetsService;
        this._ordersService = params.ordersService;
        this._pricingService = params.pricingService;
        this._fetchival = params.fetchival;
        this._feeBps = (params.feePercentage || DEFAULT_FEE_PERCENTAGE) * 100;
        this._referralProgramAddress = params.referralProgramAddress || DEFAULT_REFERRAL_PROGRAM_ADDRESS;
        this._tracer = params.tracer;
        this._assetsService.getAllByNetworks(SUPPORTED_NETWORKS, { limit: 1000 }).catch((err) => {
            console.error('Failed to get initial assets', err);
        });
    }
    async getSupportedFromAssetIds() {
        return this._getAssets();
    }
    async getSupportedToAssetIds(from) {
        const tokens = await this.getSupportedFromAssetIds();
        return tokens.has(from) ? tokens : new Set();
    }
    async isPairSupported(pairId) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (fromAsset?.network !== toAsset?.network) {
            return false;
        }
        const tokens = await this._getAssets();
        return tokens.has(from) && tokens.has(to);
    }
    async getQuote(params) {
        const { amount, pairId, slippage, swapMode = SwapMode.ExactIn } = params;
        const quoteCacheKey = serializeQuoteKey({ amount, pairId, slippage, swapMode });
        const cachedQuote = this._cachedQuotes.get(quoteCacheKey);
        if (!cachedQuote || cachedQuote.expiry < Date.now()) {
            const quote = await this._getQuote(params);
            this._cachedQuotes.set(quoteCacheKey, { quote, expiry: Date.now() + QUOTE_CACHE_TTL });
        }
        return this._cachedQuotes.get(quoteCacheKey).quote;
    }
    async _getQuote(params) {
        const { amount, includePartners, excludePartners, swapMode = SwapMode.ExactIn, pairId, slippage = DEFAULT_SLIPPAGE, } = params;
        const { from, to } = deconstructPairId(pairId);
        const [quote, limits, feeAsset, toAsset] = await Promise.all([
            this._getQuoteFromJupiter({
                pairId,
                excludeDexes: excludePartners,
                dexes: includePartners,
                amount,
                slippage: slippage * 100,
                swapMode,
            }),
            this.getLimits({ pairId }),
            this._assetsService.getOneById('SOL'),
            this._assetsService.getOneById(to),
        ]);
        if (!feeAsset) {
            throw new Error('missing fee asset');
        }
        if (!toAsset) {
            throw new Error(`Could not get asset ${to}`);
        }
        const limit = swapMode === SwapMode.ExactIn ? limits[0]?.ExactIn : limits[0]?.ExactOut;
        if (!limit || amount.lt(limit.min) || amount.gt(limit.max)) {
            const minValue = limit?.min.toDefaultString() || 'UNDEF_VAL';
            const maxValue = limit?.max.toDefaultString() || 'UNDEF_VAL';
            const err = new Error(`No rate found for ${amount.toDefaultString()} (min: ${minValue} - max: ${maxValue})`);
            err.name = 'NO_RATE_AVAILABLE';
            throw err;
        }
        const tokenAccountsFee = calculateTokenAccountsFee(from, to);
        const rate = swapMode === SwapMode.ExactIn
            ? quote.amount.div(amount.toDefaultString()).toDefaultString()
            : amount.div(quote.amount.toDefaultString()).toDefaultString();
        return {
            amount: quote.amount,
            minerFee: toAsset.currency.defaultUnit(0),
            providerName: this.name,
            rateId: quote.rateId,
            rate,
            exchangeFeeData: {
                extraFees: {
                    tokenAccountsFee: feeAsset.currency.defaultUnit(tokenAccountsFee),
                },
            },
            exchangeTxData: {
                bestRoute: quote.bestRoute,
            },
        };
    }
    async prepareExchange({ pairId, fromAmount, toAmount, fromAddress, toAddress, slippage = DEFAULT_SLIPPAGE, anonymousId, referralCode, swapMode = SwapMode.ExactIn, onOrderIdGenerated, signRawTx, simulateTx, quote, }) {
        try {
            const traceMessage = this._traceMessageScope('prepareExchange');
            const { from, to } = deconstructPairId(pairId);
            traceMessage('Creating order in the exchange-server');
            this._tracer?.traceTime('LogDexOrder');
            const orderPayload = {
                fromAmount: fromAmount.toDefaultString({ unit: false }),
                fromAddress,
                toAddress,
                toAmount: toAmount.toDefaultString({ unit: false }),
                pairId,
                slippage,
                svc: SVC,
                referralCode,
                swapMode,
            };
            const orderPromise = this._ordersService
                .insertDex(orderPayload, { anonymousId })
                .catch(async (err) => {
                const errorMessage = await parseFetchivalError(err);
                throw new CreateOrderError({
                    message: errorMessage,
                    details: orderPayload,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('LogDexOrder'));
            const [order, asset] = await Promise.all([
                orderPromise,
                this._assetsService.getOneById(swapMode === SwapMode.ExactIn ? to : from),
            ]);
            if (typeof onOrderIdGenerated === 'function') {
                onOrderIdGenerated(order.id);
            }
            if (!asset) {
                throw new Error(`Could not get asset ${swapMode === SwapMode.ExactIn ? to : from}`);
            }
            this._tracer?.traceTime('GetTokenAccount');
            const tokenAccount = await this._getFeeTokenAccount(asset.solana?.mintAddress || WRAPPED_SOL_ADDRESS).finally(() => this._tracer?.traceTimeEnd('GetTokenAccount'));
            traceMessage('Getting tx from Jupiter');
            this._tracer?.traceTime('GetDexSwapTx');
            const swapPayload = {
                quoteResponse: quote.exchangeTxData.bestRoute,
                userPublicKey: fromAddress,
                feeAccount: tokenAccount,
                wrapUnwrapSOL: true,
                asLegacyTransaction: false,
                prioritizationFeeLamports: {
                    priorityLevelWithMaxLamports: {
                        maxLamports: 4_000_000,
                        global: false,
                        priorityLevel: 'veryHigh',
                    },
                },
                dynamicComputeUnitLimit: true,
            };
            const { swapTransaction } = await this._request(Urls.Swap)
                .post(swapPayload)
                .catch(async (err) => {
                const errorMessage = await parseFetchivalError(err);
                throw new CreateDexTxError({
                    message: errorMessage,
                    details: swapPayload,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('GetDexSwapTx'));
            traceMessage('Checking if order is valid');
            this._tracer?.traceTime('CheckOrderIsValid');
            const orderCheckPromise = this._checkOrderIsValid({
                order: { ...order, swapMode, slippage },
                transaction: swapTransaction,
                simulateTx,
            })
                .catch((err) => {
                traceMessage(`Failed to validate order.\nError: ${err.message}\nTransaction: ${swapTransaction}`);
                throw new OrderValidationError({
                    message: err.message,
                    order: { ...order, swapMode, slippage },
                    providerOrder: swapTransaction,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CheckOrderIsValid'));
            this._tracer?.traceTime('UpdateBlockhash');
            const rawTxPromise = updateBlockhash(swapTransaction)
                .catch((err) => {
                traceMessage(`Soft error: failed to update blockhash.\nError: ${err.message}`);
            })
                .finally(() => this._tracer?.traceTimeEnd('UpdateBlockhash'));
            const [rawTx] = await Promise.all([rawTxPromise, orderCheckPromise]);
            traceMessage('Signing raw tx');
            this._tracer?.traceTime('SignTx');
            const signedRawTx = await signRawTx({
                assetId: from,
                rawTx: rawTx || swapTransaction,
            })
                .catch((err) => {
                throw new SignTxError({ message: err.message });
            })
                .finally(() => this._tracer?.traceTimeEnd('SignTx'));
            return {
                orderId: order.id,
                transaction: signedRawTx,
                fromAmount: swapMode === SwapMode.ExactIn ? fromAmount : quote.amount,
            };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async executeExchange({ pairId, transaction, orderId, broadcastRawTx, }) {
        try {
            const traceMessage = this._traceMessageScope('executeExchange');
            const { from } = deconstructPairId(pairId);
            traceMessage('Broadcasting raw tx');
            this._tracer?.traceTime('BroadcastTx');
            const { txId } = await broadcastRawTx({ assetId: from, rawTx: transaction })
                .catch((err) => {
                throw new SendTxError({ message: err.message });
            })
                .finally(() => this._tracer?.traceTimeEnd('BroadcastTx'));
            traceMessage('Updating order with txId');
            this._ordersService
                .updateDex({
                id: orderId,
                transactionId: txId,
            })
                .catch((err) => console.warn(`Fail to update order ${orderId} with txId ${txId}`, err));
            return {
                transactions: [{ id: txId, type: 'swapTransaction' }],
                svc: SVC,
                providerOrderId: txId,
            };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async getLimits(params) {
        const { from, to } = deconstructPairId(params.pairId);
        const [fromAsset, toAsset, usdPrices] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
            this._pricingService.getPrices([from, to]),
        ]);
        if (!fromAsset || !toAsset) {
            throw new Error(`unknown pair ${params.pairId}`);
        }
        const minAmountUSD = 10;
        const maxAmountUSD = 100_000;
        const fromAssetPrice = usdPrices[from];
        const toAssetPrice = usdPrices[to];
        if (!fromAssetPrice || !toAssetPrice) {
            throw new Error(`Missing usd price for ${fromAssetPrice ? to : from}`);
        }
        const exactInMinAmount = minAmountUSD / Number(fromAssetPrice);
        const exactInMaxAmount = maxAmountUSD / Number(fromAssetPrice);
        const exactOutMinAmount = minAmountUSD / Number(toAssetPrice);
        const exactOutMaxAmount = maxAmountUSD / Number(toAssetPrice);
        return [
            {
                [SwapMode.ExactIn]: {
                    min: fromAsset.currency.defaultUnit(exactInMinAmount),
                    max: fromAsset.currency.defaultUnit(exactInMaxAmount),
                },
                [SwapMode.ExactOut]: {
                    min: toAsset.currency.defaultUnit(exactOutMinAmount),
                    max: toAsset.currency.defaultUnit(exactOutMaxAmount),
                },
            },
        ];
    }
    setGeolocation(_geolocation) {
    }
    _getFeeTokenAccount = memoizeWithTimeout(async (tokenAddress) => {
        const referralProgramPubkey = new PublicKey(this._referralProgramAddress);
        const tokenPubkey = new PublicKey(tokenAddress);
        const referralProgramIdPubkey = new PublicKey(REFERRAL_PROGRAM_ID);
        const [feeAccount] = PublicKey.findProgramAddressSync([Buffer.from('referral_ata'), referralProgramPubkey.toBuffer(), tokenPubkey.toBuffer()], referralProgramIdPubkey);
        const accountInfo = await promiseWithTimeout({
            promise: solanaApi.getAccountInfo(feeAccount),
            delayMs: 3000,
        }).catch(() => null);
        const tokenAccountExists = !!accountInfo?.data;
        return tokenAccountExists ? feeAccount?.toBase58() : undefined;
    });
    _getUsdPrices = memoizeWithTimeout(async (ids) => {
        const res = await this._request(Urls.Price).get({ ids });
        return res.data;
    }, (ids) => ids.join(','));
    async _getQuoteFromJupiter({ pairId, amount, slippage: slippageBps, excludeDexes = [], dexes = [], swapMode = SwapMode.ExactIn, }) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (!fromAsset?.decimals || !toAsset?.decimals) {
            throw new TypeError(`missing \`decimals\` for ${fromAsset?.decimals ? to : from} asset`);
        }
        const inputMint = fromAsset.solana?.mintAddress || WRAPPED_SOL_ADDRESS;
        const outputMint = toAsset.solana?.mintAddress || WRAPPED_SOL_ADDRESS;
        const feeAddress = await this._getFeeTokenAccount(swapMode === SwapMode.ExactIn ? outputMint : inputMint);
        const quote = await this._request(Urls.Quotes)
            .get({
            inputMint,
            outputMint,
            amount: amount.toBaseString({ unit: false }),
            swapMode,
            slippageBps,
            restrictIntermediateTokens: true,
            ...(feeAddress ? { platformFeeBps: this._feeBps } : {}),
            ...(dexes.length > 0 ? { dexes } : excludeDexes.length > 0 ? { excludeDexes } : {}),
        })
            .catch((err) => {
            console.error(err);
            throw new Error(`Unable to get Jupiter quote for ${amount.toDefaultString()} ${from} -> ${to}: ${err.message}`);
        });
        if ('error' in quote) {
            throw new Error(quote.error);
        }
        const feeAsset = await this._assetsService.getOneById('SOL');
        if (!feeAsset) {
            throw new Error('missing fee asset');
        }
        let quotedAmount;
        if (swapMode === SwapMode.ExactOut) {
            quotedAmount = fromAsset.currency.baseUnit(quote.inAmount || 0);
        }
        else {
            quotedAmount = toAsset.currency.baseUnit(quote.outAmount || 0);
        }
        const rateId = `${pairId}_${quotedAmount.toDefaultString({ unit: false })}`;
        return {
            bestRoute: quote,
            amount: quotedAmount,
            rateId,
        };
    }
    _getAssets = memoizeWithTimeout(async () => {
        return new Set(await this._assetsService.getAssetIdsBySource(this.name));
    });
    _calculateSlippagePercentage({ initialAmount, actualAmount, }) {
        const spread = initialAmount.sub(actualAmount);
        return spread.div(actualAmount.toDefaultNumber()).mul(100).toDefaultNumber();
    }
    async _checkOrderIsValid({ order, transaction, simulateTx, }) {
        const { from, to } = deconstructPairId(order.pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new Error('missing pair');
        }
        const deserializedTranasction = deserializeTransaction(transaction);
        const { balanceChanges: { willSend = [], willReceive = [] } = {} } = await simulateTx({
            baseAssetName: 'solana',
            transactionBuffers: [deserializedTranasction.serialize()],
            senderAddress: order.fromAddress,
            origin: ORIGIN,
        });
        if (willSend.length === 0 || willReceive.length === 0) {
            throw new Error(`Invalid Order: unable to validate order (${transaction})`);
        }
        const isFromSOL = fromAsset.id === 'SOL';
        const sendingBalance = isFromSOL
            ? willSend
                .reduce((acc, { balance }) => acc.add(balance), fromAsset.currency.defaultUnit(0))
            : willSend
                .filter(({ asset }) => asset.address === fromAsset.solana?.mintAddress)
                .reduce((acc, { balance }) => {
                const balanceInAssetUnit = fromAsset.currency.baseUnit(balance.toBaseString());
                return acc.add(balanceInAssetUnit);
            }, fromAsset.currency.defaultUnit(0));
        const orderAmountNU = fromAsset.currency.defaultUnit(order.amount.value);
        const maxAllowedToSpend = isFromSOL
            ? orderAmountNU.add(fromAsset.currency.defaultUnit(NEW_TOKEN_ACCOUNT_FEE * 2))
            : orderAmountNU;
        const slippageEquivalent = order.swapMode === SwapMode.ExactOut
            ? fromAsset.currency.defaultUnit(order.amount.value).mul(100 - order.slippage)
            : fromAsset.currency.ZERO;
        const isValidSendingBalance = maxAllowedToSpend.add(slippageEquivalent).gte(sendingBalance);
        if (!isValidSendingBalance) {
            throw new Error(`Invalid Order: ${sendingBalance.toDefaultString({ unit: false })} !== ${order.amount.value} (${transaction})`);
        }
        const isValidFromToken = willSend.every((send) => [fromAsset.solana?.mintAddress || SOL_ADDRESS, SOL_ADDRESS, WRAPPED_SOL_ADDRESS].includes(send.asset.address));
        if (!isValidFromToken) {
            throw new Error(`Invalid Order: FROM does not match: ${JSON.stringify(willSend)} (${transaction})`);
        }
        const isValidToToken = willReceive.every((receive) => [toAsset.solana?.mintAddress || SOL_ADDRESS, SOL_ADDRESS, WRAPPED_SOL_ADDRESS].includes(receive.asset.address));
        if (!isValidToToken) {
            throw new Error(`Invalid Order: TO does not match: ${JSON.stringify(willReceive)} (${transaction})`);
        }
    }
    _request(url) {
        return this._fetchival(url);
    }
    _traceMessageScope(scope) {
        return (message) => this._tracer?.traceMessage(`${this.name}:${scope}:${message}`);
    }
}
export default Jupiter;
